import React, { useState, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { refreshUrl } from '../../../api/documents';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tooltip,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Publish as PublishIcon,
  Unpublished as UnpublishIcon,
  AutoAwesome as AIIcon,
  Article as ArticleIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as ViewIcon,
  CloudUpload as UploadIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  Lightbulb as SuggestIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { blogApi } from '../../../api/marketing';
import { Blog, BlogStats, BlogContentImage } from '../../../types/marketing.types';

// Component to handle S3 images with automatic URL refresh on 403
const S3Image: React.FC<{ src: string; alt: string; sx?: any }> = ({ src, alt, sx }) => {
  const [imgSrc, setImgSrc] = useState(src);
  const [retryCount, setRetryCount] = useState(0);
  const [key, setKey] = useState(0);

  useEffect(() => {
    setImgSrc(src);
    setRetryCount(0);
    setKey(0);
  }, [src]);

  const handleError = async () => {
    if (retryCount < 2) {
      try {
        const freshUrl = await refreshUrl(imgSrc);
        setImgSrc(freshUrl);
        setRetryCount(prev => prev + 1);
        setKey(prev => prev + 1);
      } catch (error) {
        console.error('Failed to refresh image URL:', error);
      }
    }
  };

  return <Box key={key} component="img" src={imgSrc} alt={alt} sx={sx} onError={handleError} />;
};

const BlogsPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [blogs, setBlogs] = useState<Blog[]>([]);
  const [stats, setStats] = useState<BlogStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [openDialog, setOpenDialog] = useState(false);
  const [openAIDialog, setOpenAIDialog] = useState(false);
  const [editingBlog, setEditingBlog] = useState<Blog | null>(null);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [previewBlog, setPreviewBlog] = useState<Blog | null>(null);
  const [featuredImage, setFeaturedImage] = useState<string>('');
  const [contentImages, setContentImages] = useState<BlogContentImage[]>([]);
  const [imageGenerating, setImageGenerating] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [imagePrompt, setImagePrompt] = useState('');
  const [suggestingPlacement, setSuggestingPlacement] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    contentMarkdown: '',
    excerpt: '',
    tags: '',
    category: '',
    socialMediaTeaser: '',
    seo: {
      metaTitle: '',
      metaDescription: '',
      keywords: [] as string[]
    }
  });
  const [aiPrompt, setAiPrompt] = useState({
    prompt: '',
    tone: 'professional' as 'professional' | 'casual' | 'technical' | 'friendly',
    length: 'medium' as 'short' | 'medium' | 'long',
    keywords: '',
    targetAudience: '',
    ctaGoal: '',
    imagePromptText: '',
    generateImage: true,
    imageSource: 'ai' as 'ai' | 'upload' | 'none'
  });
  const [aiUploadedImage, setAiUploadedImage] = useState<{ url: string; s3Key: string } | null>(null);
  const [aiImageUploading, setAiImageUploading] = useState(false);

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await blogApi.getAll({
        page: page + 1,
        limit: rowsPerPage,
        search: searchTerm || undefined,
        status: statusFilter || undefined
      });

      if (response.success && response.data) {
        setBlogs(response.data);
        setTotalCount(response.pagination?.total || 0);
      }
    } catch (error) {
      enqueueSnackbar('Failed to fetch blogs', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, statusFilter, enqueueSnackbar]);

  useEffect(() => {
    fetchBlogs();
    fetchStats();
  }, [fetchBlogs]);

  const fetchStats = async () => {
    try {
      const response = await blogApi.getStats();
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleOpenDialog = (blog?: Blog) => {
    if (blog) {
      setEditingBlog(blog);
      setFormData({
        title: blog.title,
        slug: blog.slug,
        contentMarkdown: blog.contentMarkdown || blog.content || '',
        excerpt: blog.excerpt || '',
        tags: blog.tags.join(', '),
        category: blog.category || '',
        socialMediaTeaser: blog.socialMediaTeaser || '',
        seo: {
          metaTitle: blog.seo.metaTitle || '',
          metaDescription: blog.seo.metaDescription || '',
          keywords: blog.seo.keywords || []
        }
      });
      setFeaturedImage(blog.featuredImage || '');
      setContentImages(blog.contentImages || []);
    } else {
      setEditingBlog(null);
      setFormData({
        title: '',
        slug: '',
        contentMarkdown: '',
        excerpt: '',
        tags: '',
        category: '',
        socialMediaTeaser: '',
        seo: {
          metaTitle: '',
          metaDescription: '',
          keywords: []
        }
      });
      setFeaturedImage('');
      setContentImages([]);
    }
    setImagePrompt('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingBlog(null);
    setFeaturedImage('');
    setContentImages([]);
    setImagePrompt('');
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
        featuredImage: featuredImage || undefined,
        contentImages
      };
      if (editingBlog) {
        await blogApi.update(editingBlog._id, payload);
        enqueueSnackbar('Blog updated successfully', { variant: 'success' });
      } else {
        await blogApi.create(payload);
        enqueueSnackbar('Blog created successfully', { variant: 'success' });
      }
      handleCloseDialog();
      fetchBlogs();
      fetchStats();
    } catch (error) {
      enqueueSnackbar('Failed to save blog', { variant: 'error' });
    }
  };

  const handleGenerateImage = async () => {
    if (!imagePrompt.trim()) {
      enqueueSnackbar('Please enter an image prompt', { variant: 'warning' });
      return;
    }
    try {
      setImageGenerating(true);
      const response = await blogApi.generateImage({ prompt: imagePrompt });
      if (response.success && response.data) {
        const newImage: BlogContentImage = {
          url: response.data.url,
          s3Key: response.data.s3Key,
          alt: imagePrompt,
          placement: 'hero',
          size: 'full-width',
          insertAfterParagraph: 0,
          aiGenerated: true,
          aiPrompt: imagePrompt
        };
        setContentImages(prev => [...prev, newImage]);
        if (!featuredImage) {
          setFeaturedImage(response.data.url);
        }
        setImagePrompt('');
        enqueueSnackbar('Image generated successfully!', { variant: 'success' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to generate image', { variant: 'error' });
    } finally {
      setImageGenerating(false);
    }
  };

  const handleUploadImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setImageUploading(true);
      const response = await blogApi.uploadImage(file);
      if (response.success && response.data) {
        const newImage: BlogContentImage = {
          url: response.data.url,
          s3Key: response.data.s3Key,
          alt: file.name.replace(/\.[^/.]+$/, ''),
          placement: 'hero',
          size: 'full-width',
          insertAfterParagraph: 0,
          aiGenerated: false
        };
        setContentImages(prev => [...prev, newImage]);
        if (!featuredImage) {
          setFeaturedImage(response.data.url);
        }
        enqueueSnackbar('Image uploaded successfully!', { variant: 'success' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to upload image', { variant: 'error' });
    } finally {
      setImageUploading(false);
      event.target.value = '';
    }
  };

  const handleRemoveImage = (index: number) => {
    setContentImages(prev => {
      const updated = prev.filter((_, i) => i !== index);
      if (featuredImage === prev[index]?.url) {
        setFeaturedImage(updated[0]?.url || '');
      }
      return updated;
    });
  };

  const handleUpdateImagePlacement = (index: number, field: keyof BlogContentImage, value: any) => {
    setContentImages(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSuggestPlacement = async (index: number) => {
    const image = contentImages[index];
    if (!formData.contentMarkdown) {
      enqueueSnackbar('Please add blog content first', { variant: 'warning' });
      return;
    }
    try {
      setSuggestingPlacement(String(index));
      const description = image.aiPrompt || image.alt || 'Blog image';
      const response = await blogApi.suggestImagePlacement(formData.contentMarkdown, description);
      if (response.success && response.data) {
        const suggestion = response.data;
        setContentImages(prev => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            placement: suggestion.placement,
            size: suggestion.size,
            insertAfterParagraph: suggestion.insertAfterParagraph
          };
          return updated;
        });
        enqueueSnackbar(`AI suggests: ${suggestion.placement} (${suggestion.size}) — ${suggestion.reasoning}`, { variant: 'info', autoHideDuration: 6000 });
      }
    } catch (error) {
      enqueueSnackbar('Failed to get placement suggestion', { variant: 'error' });
    } finally {
      setSuggestingPlacement(null);
    }
  };

  const handleAiDialogUploadImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      setAiImageUploading(true);
      const response = await blogApi.uploadImage(file);
      if (response.success && response.data) {
        setAiUploadedImage({ url: response.data.url, s3Key: response.data.s3Key });
        setAiPrompt(prev => ({ ...prev, imageSource: 'upload' }));
        enqueueSnackbar('Image uploaded!', { variant: 'success' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to upload image', { variant: 'error' });
    } finally {
      setAiImageUploading(false);
      event.target.value = '';
    }
  };

  const handleGenerateAI = async () => {
    try {
      setGeneratingAI(true);

      const imgPrompt = aiPrompt.imagePromptText.trim() || aiPrompt.prompt.trim();
      const shouldGenerateAiImage = aiPrompt.imageSource === 'ai';
      const hasUploadedImage = aiPrompt.imageSource === 'upload' && aiUploadedImage;

      // Run blog content generation (and optionally AI image generation) in parallel
      const blogPromise = blogApi.generateWithAI({
        ...aiPrompt,
        keywords: aiPrompt.keywords.split(',').map(k => k.trim()).filter(k => k)
      });

      const imagePromise = shouldGenerateAiImage
        ? blogApi.generateImage({ prompt: imgPrompt }).catch(() => {
            enqueueSnackbar('Image generation failed — blog created without image', { variant: 'warning' });
            return null;
          })
        : Promise.resolve(null);

      const [blogResponse, imageResponse] = await Promise.all([blogPromise, imagePromise]);

      if (blogResponse.success && blogResponse.data) {
        // Determine which image to attach
        let imageToAttach: { url: string; s3Key: string; aiGenerated: boolean; prompt: string } | null = null;

        if (imageResponse && imageResponse.success && imageResponse.data) {
          imageToAttach = {
            url: imageResponse.data.url,
            s3Key: imageResponse.data.s3Key,
            aiGenerated: true,
            prompt: imgPrompt
          };
        } else if (hasUploadedImage) {
          imageToAttach = {
            url: aiUploadedImage.url,
            s3Key: aiUploadedImage.s3Key,
            aiGenerated: false,
            prompt: ''
          };
        }

        if (imageToAttach) {
          try {
            await blogApi.update(blogResponse.data._id, {
              featuredImage: imageToAttach.url,
              contentImages: [{
                url: imageToAttach.url,
                s3Key: imageToAttach.s3Key,
                alt: imageToAttach.aiGenerated ? imgPrompt : 'Blog image',
                placement: 'hero',
                size: 'full-width',
                insertAfterParagraph: 0,
                aiGenerated: imageToAttach.aiGenerated,
                aiPrompt: imageToAttach.prompt || undefined
              }]
            });
          } catch {
            // Image attachment failed but blog was created
          }
        }

        enqueueSnackbar(
          imageToAttach
            ? 'Blog and image created successfully!'
            : 'Blog generated successfully!',
          { variant: 'success' }
        );
        setOpenAIDialog(false);
        setAiUploadedImage(null);
        fetchBlogs();
        fetchStats();
      }
    } catch (error) {
      enqueueSnackbar('Failed to generate blog', { variant: 'error' });
    } finally {
      setGeneratingAI(false);
    }
  };

  const handlePublish = async (id: string) => {
    try {
      await blogApi.publish(id);
      enqueueSnackbar('Blog published successfully', { variant: 'success' });
      fetchBlogs();
      fetchStats();
    } catch (error) {
      enqueueSnackbar('Failed to publish blog', { variant: 'error' });
    }
  };

  const handleUnpublish = async (id: string) => {
    if (!window.confirm('Are you sure you want to unpublish this blog? It will be removed from the public website.')) {
      return;
    }

    try {
      await blogApi.unpublish(id);
      enqueueSnackbar('Blog unpublished successfully', { variant: 'success' });
      fetchBlogs();
      fetchStats();
    } catch (error) {
      enqueueSnackbar('Failed to unpublish blog', { variant: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) {
      return;
    }

    try {
      await blogApi.delete(id);
      enqueueSnackbar('Blog deleted successfully', { variant: 'success' });
      fetchBlogs();
      fetchStats();
    } catch (error) {
      enqueueSnackbar('Failed to delete blog', { variant: 'error' });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PUBLISHED':
        return 'success';
      case 'DRAFT':
        return 'warning';
      case 'ARCHIVED':
        return 'default';
      default:
        return 'default';
    }
  };

  const textFieldStyles = {
    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'white' },
    '& .MuiOutlinedInput-root': {
      color: 'white',
      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.3)' },
      '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
      '&.Mui-focused fieldset': { borderColor: 'white' }
    },
    '& .MuiFormHelperText-root': { color: 'rgba(255, 255, 255, 0.6)' }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Blogs</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AIIcon />}
            onClick={() => setOpenAIDialog(true)}
          >
            Generate with AI
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Create Blog
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      Total Blogs
                    </Typography>
                    <Typography variant="h4">{stats.totalBlogs}</Typography>
                  </Box>
                  <ArticleIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      Published
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {stats.publishedBlogs}
                    </Typography>
                  </Box>
                  <PublishIcon sx={{ fontSize: 40, color: 'success.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      AI Generated
                    </Typography>
                    <Typography variant="h4">{stats.aiGeneratedBlogs}</Typography>
                  </Box>
                  <AIIcon sx={{ fontSize: 40, color: 'secondary.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">
                      Total Views
                    </Typography>
                    <Typography variant="h4" color="primary.main">
                      {stats.totalViews}
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search blogs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="PUBLISHED">Published</MenuItem>
                <MenuItem value="DRAFT">Draft</MenuItem>
                <MenuItem value="ARCHIVED">Archived</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Blogs Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Views</TableCell>
              <TableCell>AI Generated</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : blogs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No blogs found
                </TableCell>
              </TableRow>
            ) : (
              blogs.map((blog) => (
                <TableRow key={blog._id}>
                  <TableCell>{blog.title}</TableCell>
                  <TableCell>
                    <Chip
                      label={blog.status}
                      color={getStatusColor(blog.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{blog.category || '-'}</TableCell>
                  <TableCell>{blog.viewCount}</TableCell>
                  <TableCell>
                    {blog.aiGenerated ? (
                      <Chip icon={<AIIcon />} label="AI" size="small" color="secondary" />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(blog.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="View">
                      <IconButton
                        size="small"
                        onClick={() => setPreviewBlog(blog)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    {blog.status === 'DRAFT' && (
                      <Tooltip title="Publish">
                        <IconButton
                          size="small"
                          onClick={() => handlePublish(blog._id)}
                          color="success"
                        >
                          <PublishIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {blog.status === 'PUBLISHED' && (
                      <Tooltip title="Unpublish">
                        <IconButton
                          size="small"
                          onClick={() => handleUnpublish(blog._id)}
                          color="warning"
                        >
                          <UnpublishIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(blog)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(blog._id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* Blog Preview Dialog */}
      <Dialog
        open={!!previewBlog}
        onClose={() => setPreviewBlog(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#fff',
            color: '#1a1a1a',
            minHeight: '60vh',
            maxHeight: '90vh'
          }
        }}
      >
        {previewBlog && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#1a1a1a' }}>
                {previewBlog.title}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Chip
                  label={previewBlog.status}
                  color={getStatusColor(previewBlog.status) as any}
                  size="small"
                />
                {previewBlog.category && (
                  <Chip label={previewBlog.category} size="small" variant="outlined" sx={{ borderColor: '#94a3b8', color: '#475569' }} />
                )}
                {previewBlog.tags?.map((tag) => (
                  <Chip key={tag} label={tag} size="small" variant="outlined" sx={{ fontSize: '0.75rem', borderColor: '#94a3b8', color: '#475569' }} />
                ))}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  {new Date(previewBlog.createdAt).toLocaleDateString('en-GB', {
                    day: 'numeric', month: 'long', year: 'numeric'
                  })}
                </Typography>
              </Box>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: 2 }}>
              {/* Featured Image - only show if not already in content images */}
              {previewBlog.featuredImage && !previewBlog.contentImages?.some(img => img.url === previewBlog.featuredImage) && (
                <S3Image
                  src={previewBlog.featuredImage}
                  alt={previewBlog.title}
                  sx={{
                    width: '100%',
                    maxHeight: 300,
                    objectFit: 'cover',
                    borderRadius: 1,
                    mb: 2
                  }}
                />
              )}
              {previewBlog.excerpt && (
                <Typography
                  variant="subtitle1"
                  sx={{ mb: 2, fontStyle: 'italic', color: 'text.secondary', lineHeight: 1.6 }}
                >
                  {previewBlog.excerpt}
                </Typography>
              )}
              {/* Rendered content with images at placements */}
              {(() => {
                const markdown = previewBlog.contentMarkdown || previewBlog.content || '';
                const images = previewBlog.contentImages || [];
                const nonHeroImages = images.filter(img => img.placement !== 'hero');

                // Split markdown into sections by double newline (paragraphs/blocks)
                const sections = markdown.split(/\n\n+/).filter((s: string) => s.trim());
                const totalSections = sections.length;

                // Determine insertion indices for each placement
                const insertions: Record<number, typeof nonHeroImages> = {};
                nonHeroImages.forEach(img => {
                  let idx: number;
                  switch (img.placement) {
                    case 'after-intro':
                      idx = Math.min(1, totalSections);
                      break;
                    case 'mid-content':
                      idx = Math.floor(totalSections / 2);
                      break;
                    case 'before-cta':
                      idx = Math.max(0, totalSections - 1);
                      break;
                    case 'inline':
                      idx = Math.min(img.insertAfterParagraph || 1, totalSections);
                      break;
                    default:
                      idx = 1;
                  }
                  if (!insertions[idx]) insertions[idx] = [];
                  insertions[idx].push(img);
                });

                const getImageSx = (size: string) => {
                  switch (size) {
                    case 'full-width':
                      return { width: '100%', maxHeight: 400, objectFit: 'cover' as const, borderRadius: 1, my: 2 };
                    case 'medium':
                      return { width: '70%', maxHeight: 350, objectFit: 'cover' as const, borderRadius: 1, my: 2, mx: 'auto', display: 'block' };
                    case 'small-right':
                      return { width: '40%', maxHeight: 250, objectFit: 'cover' as const, borderRadius: 1, float: 'right' as const, ml: 2, mb: 1.5 };
                    case 'small-left':
                      return { width: '40%', maxHeight: 250, objectFit: 'cover' as const, borderRadius: 1, float: 'left' as const, mr: 2, mb: 1.5 };
                    default:
                      return { width: '100%', maxHeight: 400, objectFit: 'cover' as const, borderRadius: 1, my: 2 };
                  }
                };

                const markdownStyles = {
                  '& h1': { fontSize: '2rem', fontWeight: 700, mt: 3, mb: 1.5 },
                  '& h2': { fontSize: '1.5rem', fontWeight: 600, mt: 2.5, mb: 1 },
                  '& h3': { fontSize: '1.25rem', fontWeight: 600, mt: 2, mb: 1 },
                  '& p': { lineHeight: 1.8, mb: 1.5, fontSize: '1rem' },
                  '& ul, & ol': { pl: 3, mb: 1.5 },
                  '& li': { mb: 0.5, lineHeight: 1.7 },
                  '& blockquote': {
                    borderLeft: '4px solid',
                    borderColor: 'primary.main',
                    pl: 2,
                    ml: 0,
                    my: 2,
                    fontStyle: 'italic',
                    color: 'text.secondary'
                  },
                  '& code': {
                    backgroundColor: '#f5f5f5',
                    px: 0.5,
                    py: 0.25,
                    borderRadius: 0.5,
                    fontSize: '0.875rem'
                  },
                  '& a': { color: 'primary.main', textDecoration: 'underline' },
                  '& strong': { fontWeight: 600 }
                };

                // If no non-hero images, render normally
                if (nonHeroImages.length === 0) {
                  return (
                    <Box sx={markdownStyles}>
                      <ReactMarkdown>{markdown}</ReactMarkdown>
                    </Box>
                  );
                }

                // Render sections with images inserted at correct positions
                const elements: React.ReactNode[] = [];
                sections.forEach((section: string, idx: number) => {
                  elements.push(
                    <Box key={`section-${idx}`} sx={{ ...markdownStyles, clear: 'both' }}>
                      <ReactMarkdown>{section}</ReactMarkdown>
                    </Box>
                  );
                  // Insert images after this section
                  if (insertions[idx + 1]) {
                    insertions[idx + 1].forEach((img, imgIdx) => {
                      const needsFloat = img.size === 'small-right' || img.size === 'small-left';
                      elements.push(
                        <Box key={`img-${idx}-${imgIdx}`} sx={needsFloat ? {} : { clear: 'both' }}>
                          <S3Image
                            src={img.url}
                            alt={img.alt}
                            sx={getImageSx(img.size)}
                          />
                          {img.caption && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5, mb: 1, fontStyle: 'italic' }}>
                              {img.caption}
                            </Typography>
                          )}
                        </Box>
                      );
                    });
                  }
                });
                // Clear any remaining floats
                elements.push(<Box key="clear-end" sx={{ clear: 'both' }} />);
                return <>{elements}</>;
              })()}
              {previewBlog.socialMediaTeaser && (
                <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="caption" fontWeight="bold" color="text.secondary">
                    Social Media Teaser
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {previewBlog.socialMediaTeaser}
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <Divider />
            <DialogActions sx={{ px: 3, py: 1.5 }}>
              {previewBlog.status === 'DRAFT' && (
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<PublishIcon />}
                  onClick={() => {
                    handlePublish(previewBlog._id);
                    setPreviewBlog(null);
                  }}
                >
                  Publish
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => {
                  handleOpenDialog(previewBlog);
                  setPreviewBlog(null);
                }}
              >
                Edit
              </Button>
              <Button onClick={() => setPreviewBlog(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* AI Generation Dialog */}
      <Dialog 
        open={openAIDialog} 
        onClose={() => setOpenAIDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: 'white',
            backgroundImage: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(51, 65, 85, 0.95) 100%)'
          }
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(4px)'
            }
          }
        }}
      >
        <DialogTitle sx={{ color: 'white' }}>Generate Blog with AI</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Topic / Prompt"
              required
              fullWidth
              multiline
              rows={3}
              value={aiPrompt.prompt}
              onChange={(e) => setAiPrompt({ ...aiPrompt, prompt: e.target.value })}
              placeholder="e.g., Write about energy saving tips for businesses"
              sx={textFieldStyles}
            />
            <FormControl fullWidth sx={textFieldStyles}>
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Tone</InputLabel>
              <Select
                value={aiPrompt.tone}
                label="Tone"
                onChange={(e) => setAiPrompt({ ...aiPrompt, tone: e.target.value as any })}
                sx={{ color: 'white' }}
              >
                <MenuItem value="professional">Professional</MenuItem>
                <MenuItem value="casual">Casual</MenuItem>
                <MenuItem value="technical">Technical</MenuItem>
                <MenuItem value="friendly">Friendly</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth sx={textFieldStyles}>
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Length</InputLabel>
              <Select
                value={aiPrompt.length}
                label="Length"
                onChange={(e) => setAiPrompt({ ...aiPrompt, length: e.target.value as any })}
                sx={{ color: 'white' }}
              >
                <MenuItem value="short">Short (500-800 words)</MenuItem>
                <MenuItem value="medium">Medium (1000-1500 words)</MenuItem>
                <MenuItem value="long">Long (2000-3000 words)</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Keywords (comma separated)"
              fullWidth
              value={aiPrompt.keywords}
              onChange={(e) => setAiPrompt({ ...aiPrompt, keywords: e.target.value })}
              placeholder="energy efficiency, savings, business"
              sx={textFieldStyles}
            />
            <TextField
              label="Target Audience"
              fullWidth
              value={aiPrompt.targetAudience}
              onChange={(e) => setAiPrompt({ ...aiPrompt, targetAudience: e.target.value })}
              placeholder="e.g., business owners and energy managers"
              helperText="Who is this content for? Leave blank for default: business owners and energy managers"
              sx={textFieldStyles}
            />
            <TextField
              label="Call-to-Action Goal"
              fullWidth
              value={aiPrompt.ctaGoal}
              onChange={(e) => setAiPrompt({ ...aiPrompt, ctaGoal: e.target.value })}
              placeholder="e.g., Contact us to reduce your energy costs"
              helperText="What action should the reader take? Leave blank for default CTA"
              sx={textFieldStyles}
            />

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', my: 0.5 }} />

            {/* Image Section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ImageIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.7)' }} />
              <Typography variant="subtitle2" fontWeight="bold">
                Featured Image
              </Typography>
              <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                <Button
                  size="small"
                  variant={aiPrompt.imageSource === 'ai' ? 'contained' : 'outlined'}
                  color={aiPrompt.imageSource === 'ai' ? 'primary' : 'inherit'}
                  onClick={() => { setAiPrompt({ ...aiPrompt, imageSource: 'ai' }); setAiUploadedImage(null); }}
                  sx={{ fontSize: '0.7rem', textTransform: 'none' }}
                  startIcon={<AIIcon sx={{ fontSize: '14px !important' }} />}
                >
                  AI Generate
                </Button>
                <Button
                  size="small"
                  variant={aiPrompt.imageSource === 'upload' ? 'contained' : 'outlined'}
                  color={aiPrompt.imageSource === 'upload' ? 'primary' : 'inherit'}
                  onClick={() => setAiPrompt({ ...aiPrompt, imageSource: 'upload' })}
                  sx={{ fontSize: '0.7rem', textTransform: 'none' }}
                  startIcon={<UploadIcon sx={{ fontSize: '14px !important' }} />}
                >
                  Upload
                </Button>
                <Button
                  size="small"
                  variant={aiPrompt.imageSource === 'none' ? 'contained' : 'outlined'}
                  color="inherit"
                  onClick={() => { setAiPrompt({ ...aiPrompt, imageSource: 'none' }); setAiUploadedImage(null); }}
                  sx={{ fontSize: '0.7rem', textTransform: 'none' }}
                >
                  None
                </Button>
              </Box>
            </Box>
            {aiPrompt.imageSource === 'ai' && (
              <TextField
                label="Image Prompt (optional)"
                fullWidth
                value={aiPrompt.imagePromptText}
                onChange={(e) => setAiPrompt({ ...aiPrompt, imagePromptText: e.target.value })}
                placeholder="e.g., Modern office building with solar panels — leave blank to auto-generate from topic"
                helperText="Describe the image you want. If left blank, the blog topic will be used as the prompt."
                sx={textFieldStyles}
              />
            )}
            {aiPrompt.imageSource === 'upload' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {!aiUploadedImage ? (
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={aiImageUploading ? <CircularProgress size={16} /> : <UploadIcon />}
                    disabled={aiImageUploading}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    {aiImageUploading ? 'Uploading...' : 'Choose Image'}
                    <input type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={handleAiDialogUploadImage} />
                  </Button>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      component="img"
                      src={aiUploadedImage.url}
                      alt="Uploaded preview"
                      sx={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 1, border: '2px solid #4caf50' }}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Chip label="Uploaded" size="small" color="success" sx={{ width: 'fit-content' }} />
                      <Button
                        size="small"
                        color="error"
                        startIcon={<CloseIcon />}
                        onClick={() => { setAiUploadedImage(null); }}
                        sx={{ fontSize: '0.75rem', textTransform: 'none' }}
                      >
                        Remove
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAIDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleGenerateAI} 
            variant="contained" 
            disabled={!aiPrompt.prompt || generatingAI}
            startIcon={generatingAI ? <CircularProgress size={20} /> : <AIIcon />}
          >
            {generatingAI ? 'Generating...' : aiPrompt.imageSource !== 'none' ? 'Generate Blog + Image' : 'Generate Blog'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: 'white',
            backgroundImage: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(51, 65, 85, 0.95) 100%)'
          }
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              backdropFilter: 'blur(4px)'
            }
          }
        }}
      >
        <DialogTitle sx={{ color: 'white' }}>
          {editingBlog ? 'Edit Blog' : 'Create Blog'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Title"
              required
              fullWidth
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              sx={textFieldStyles}
            />
            <TextField
              label="Slug"
              fullWidth
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
              helperText="URL-friendly version of title (auto-generated if empty)"
              sx={textFieldStyles}
            />
            <TextField
              label="Content (Markdown)"
              required
              fullWidth
              multiline
              rows={10}
              value={formData.contentMarkdown}
              onChange={(e) => setFormData({ ...formData, contentMarkdown: e.target.value })}
              helperText="Write content in Markdown format. Use ## for headings, **bold**, *italic*, - for lists"
              sx={textFieldStyles}
            />

            {/* Images Section */}
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', my: 1 }} />
            <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ImageIcon fontSize="small" /> Images
            </Typography>

            {/* Add Image Controls */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <TextField
                label="AI Image Prompt"
                size="small"
                value={imagePrompt}
                onChange={(e) => setImagePrompt(e.target.value)}
                placeholder="e.g., Modern office with solar panels on roof"
                sx={{ ...textFieldStyles, flex: 1, minWidth: 200 }}
              />
              <Button
                variant="outlined"
                size="small"
                startIcon={imageGenerating ? <CircularProgress size={16} /> : <AIIcon />}
                onClick={handleGenerateImage}
                disabled={imageGenerating || !imagePrompt.trim()}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {imageGenerating ? 'Generating...' : 'Generate'}
              </Button>
              <Button
                variant="outlined"
                size="small"
                component="label"
                startIcon={imageUploading ? <CircularProgress size={16} /> : <UploadIcon />}
                disabled={imageUploading}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {imageUploading ? 'Uploading...' : 'Upload'}
                <input type="file" hidden accept="image/jpeg,image/png,image/webp" onChange={handleUploadImage} />
              </Button>
            </Box>

            {/* Image List */}
            {contentImages.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {contentImages.map((img, index) => (
                  <Paper
                    key={index}
                    sx={{
                      p: 1.5,
                      backgroundColor: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 1
                    }}
                  >
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      {/* Thumbnail */}
                      <S3Image
                        src={img.url}
                        alt={img.alt}
                        sx={{
                          width: 100,
                          height: 70,
                          objectFit: 'cover',
                          borderRadius: 0.5,
                          flexShrink: 0,
                          border: featuredImage === img.url ? '2px solid #4caf50' : '1px solid rgba(255,255,255,0.2)'
                        }}
                      />
                      {/* Controls */}
                      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                          {img.aiGenerated && (
                            <Chip icon={<AIIcon />} label="AI" size="small" color="secondary" sx={{ height: 22 }} />
                          )}
                          {featuredImage === img.url && (
                            <Chip label="Featured" size="small" color="success" sx={{ height: 22 }} />
                          )}
                          {featuredImage !== img.url && (
                            <Button
                              size="small"
                              variant="text"
                              sx={{ fontSize: '0.7rem', p: 0, minWidth: 'auto', color: 'rgba(255,255,255,0.7)' }}
                              onClick={() => setFeaturedImage(img.url)}
                            >
                              Set as Featured
                            </Button>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <FormControl size="small" sx={{ minWidth: 130, ...textFieldStyles }}>
                            <InputLabel sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>Placement</InputLabel>
                            <Select
                              value={img.placement}
                              label="Placement"
                              onChange={(e) => handleUpdateImagePlacement(index, 'placement', e.target.value)}
                              sx={{ color: 'white', fontSize: '0.8rem' }}
                            >
                              <MenuItem value="hero">Hero (Top)</MenuItem>
                              <MenuItem value="after-intro">After Intro</MenuItem>
                              <MenuItem value="mid-content">Mid Content</MenuItem>
                              <MenuItem value="before-cta">Before CTA</MenuItem>
                              <MenuItem value="inline">Inline</MenuItem>
                            </Select>
                          </FormControl>
                          <FormControl size="small" sx={{ minWidth: 120, ...textFieldStyles }}>
                            <InputLabel sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>Size</InputLabel>
                            <Select
                              value={img.size}
                              label="Size"
                              onChange={(e) => handleUpdateImagePlacement(index, 'size', e.target.value)}
                              sx={{ color: 'white', fontSize: '0.8rem' }}
                            >
                              <MenuItem value="full-width">Full Width</MenuItem>
                              <MenuItem value="medium">Medium</MenuItem>
                              <MenuItem value="small-right">Small Right</MenuItem>
                              <MenuItem value="small-left">Small Left</MenuItem>
                            </Select>
                          </FormControl>
                          <TextField
                            size="small"
                            label="Alt Text"
                            value={img.alt}
                            onChange={(e) => handleUpdateImagePlacement(index, 'alt', e.target.value)}
                            sx={{ ...textFieldStyles, flex: 1, minWidth: 120 }}
                            InputProps={{ sx: { fontSize: '0.8rem' } }}
                          />
                        </Box>
                      </Box>
                      {/* Action Buttons */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0 }}>
                        <Tooltip title="AI Suggest Placement">
                          <IconButton
                            size="small"
                            onClick={() => handleSuggestPlacement(index)}
                            disabled={suggestingPlacement === String(index)}
                            sx={{ color: 'rgba(255,255,255,0.7)' }}
                          >
                            {suggestingPlacement === String(index) ? <CircularProgress size={18} /> : <SuggestIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove Image">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveImage(index)}
                            sx={{ color: 'error.main' }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', my: 1 }} />

            <TextField
              label="Excerpt"
              fullWidth
              multiline
              rows={2}
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              sx={textFieldStyles}
            />
            <TextField
              label="Category"
              fullWidth
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              sx={textFieldStyles}
            />
            <TextField
              label="Tags (comma separated)"
              fullWidth
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              sx={textFieldStyles}
            />
            <TextField
              label="Social Media Teaser"
              fullWidth
              multiline
              rows={2}
              value={formData.socialMediaTeaser}
              onChange={(e) => setFormData({ ...formData, socialMediaTeaser: e.target.value })}
              helperText="One-sentence hook for LinkedIn/Twitter to drive clicks"
              sx={textFieldStyles}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!formData.title || !formData.contentMarkdown}>
            {editingBlog ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BlogsPage;
